/**
 * Targets, red/amber/green and the 12-month movement trend: wire contract and pure rules.
 *
 * NO IMPORTS, for the reason reportContract.ts gives: the hooks reach `lib/firebase` at module
 * load, so anything a test needs to assert must live where importing it cannot drag a live client
 * into the runner. Every rule a tile or chart applies to a target is here, once.
 *
 * The shapes mirror services/client-functions/src/pipelineTracker/targets/schema.ts and
 * reports/movementTrend.ts. They are duplicated rather than shared because the browser bundle
 * cannot reach the functions package and the tracker isolation fence forbids a shared one.
 *
 * MONEY IS INTEGER CENTS on the wire and in every figure below. The editor takes whole dollars and
 * multiplies by 100, so no fractional cent can be typed.
 */

/* ----------------------------------------------------------------- action names */

/** On the taxonomy callable (TAXONOMY_CALLABLE in reportContract.ts). */
export const GET_TARGETS_ACTION = 'getTargets';
export const SAVE_TARGETS_ACTION = 'saveTargets';
export const GET_MONTHLY_INPUTS_ACTION = 'getMonthlyInputs';
export const SAVE_MONTHLY_INPUTS_ACTION = 'saveMonthlyInputs';

/** On the report callable (REPORT_CALLABLE in reportContract.ts). */
export const MOVEMENT_TREND_REPORT_ACTION = 'movementTrendReport';

/* ----------------------------------------------------------------------- shapes */

export type TargetStageKey = 'leads' | 'submissions' | 'approvals' | 'settlements';
export type DatedFlowKey = 'created' | 'lodged' | 'approved' | 'settled';

/**
 * The four dated movements a target is set against, in pipeline order, with the flow each one is
 * measured by. The labels are the Deal movements row's, so a target reads against the tile it
 * sits on.
 */
export const TARGET_STAGES: ReadonlyArray<{ key: TargetStageKey; label: string; flow: DatedFlowKey }> = [
  { key: 'leads', label: 'Leads', flow: 'created' },
  { key: 'submissions', label: 'Submissions', flow: 'lodged' },
  { key: 'approvals', label: 'Approvals', flow: 'approved' },
  { key: 'settlements', label: 'Settlements', flow: 'settled' },
];

export const stageForFlow = (flow: DatedFlowKey): TargetStageKey =>
  (TARGET_STAGES.find((stage) => stage.flow === flow) as { key: TargetStageKey }).key;

export interface StageTarget {
  monthlyCount?: number;
  /** Integer cents. */
  monthlyValueCents?: number;
}

export type StageTargets = Partial<Record<TargetStageKey, StageTarget>>;

/**
 * A broker's targets: the four stages plus their own average settled deal target (integer cents).
 * Mirrors BrokerTargetsSchema: a broker with only an average deal target has no stage target, so
 * resolveTargetScope still says "No target set for this broker" for the stage tiles.
 */
export interface BrokerTargets extends StageTargets {
  averageDealCents?: number;
}

/** Mirrors PARTNER_KEY_PREFIX: a referrer target key is this plus the Referrer Ref. */
export const PARTNER_TARGET_KEY_PREFIX = 'id:';
/** Mirrors MAX_PARTNER_OVERRIDES on the server. */
export const MAX_PARTNER_OVERRIDES = 200;

export interface RagThresholds {
  greenAtPct: number;
  amberAtPct: number;
}

export const DEFAULT_RAG: RagThresholds = { greenAtPct: 100, amberAtPct: 70 };

export interface SlaTargets {
  dipHours?: number;
  mirHours?: number;
}

/**
 * Targets for the monthly inputs, one figure each, compared with the month's entered figure as it
 * stands (levels, not flows, so never pro-rated). Absent on a document saved before they existed.
 */
export interface MonthlyInputTargets {
  googleReviewCount?: number;
  googleRating?: number;
  sfgAuditPct?: number;
}

export interface TargetsDoc {
  stages: StageTargets;
  byBroker?: Record<string, BrokerTargets>;
  rag: RagThresholds;
  sla: SlaTargets;
  monthlyInputTargets?: MonthlyInputTargets;
  /** Monthly target for the company's average settled deal, integer cents. A level, never pro-rated. */
  averageDealCents?: number;
  /**
   * Monthly net settlements target, integer cents, company only. Compared against whichever loan
   * book figure the Board pack leads with (NET_SETTLEMENTS_HEADLINE in tabs/netBook.ts).
   */
  netSettlementsCents?: number;
  /** Referrer targets for the four stages, keyed by the Leads & Partners partner key (`id:<ref>`). */
  byPartner?: Record<string, StageTargets>;
  version: number;
  updatedAt: string;
  updatedBy: string;
}

export interface MonthlyInput {
  googleReviewCount?: number;
  googleRating?: number;
  sfgAuditPct?: number;
}

export interface MonthlyInputsDoc {
  months: Record<string, MonthlyInput>;
  version: number;
  updatedAt: string;
  updatedBy: string;
}

export interface FlowFigure {
  count: number;
  valueCents: number;
}

export interface MovementTrendMonth {
  /** 'YYYY-MM'. */
  month: string;
  from: string;
  to: string;
  /** The snapshot's own month, cut short at the snapshot date. */
  partial: boolean;
  created: FlowFigure;
  lodged: FlowFigure;
  approved: FlowFigure;
  settled: FlowFigure;
}

export type MovementTrendResponse =
  | { status: 'ok'; asAtDate: string; months: MovementTrendMonth[] }
  | { status: 'no_snapshot'; asAtDate: string | null; reason: string }
  | { status: 'invalid_request'; reason: string };

/* --------------------------------------------------------------------- scoping */

/**
 * Which targets apply to the view on screen.
 *
 * NEVER A SILENT FALLBACK. A broker with no override of their own is not measured against the
 * company's target: the company target is the whole practice's month, and one broker set against
 * it reads red forever. So a broker view without an override says so. The same holds for a book
 * (roster group) and for a deal type or label filter: targets are set for the whole practice or
 * for one broker, and a filtered slice measured against either would be graded on a number it was
 * never meant to meet.
 */
export type TargetScope =
  | { kind: 'company' | 'broker'; stages: StageTargets; rag: RagThresholds }
  | { kind: 'none'; message: string };

export const NO_TARGET_SET = 'No target set';
export const NO_TARGET_FOR_BROKER = 'No target set for this broker';
export const NO_TARGET_FOR_BOOK = 'No target set for a book';
export const NO_TARGET_UNDER_FILTER = 'No target under a deal type or label filter';
export const NO_TARGET_FOR_PARTNER = 'No target set for this referrer';

export function resolveTargetScope(
  targets: TargetsDoc | null | undefined,
  scope: { adviserId?: string; groupKey?: string; filtered?: boolean }
): TargetScope {
  if (!targets) return { kind: 'none', message: NO_TARGET_SET };
  const rag = targets.rag ?? DEFAULT_RAG;
  if (scope.filtered) return { kind: 'none', message: NO_TARGET_UNDER_FILTER };
  if (scope.adviserId !== undefined && scope.adviserId !== '') {
    const own = targets.byBroker?.[scope.adviserId];
    if (!own || !hasAnyTarget(own)) return { kind: 'none', message: NO_TARGET_FOR_BROKER };
    return { kind: 'broker', stages: own, rag };
  }
  if (scope.groupKey !== undefined && scope.groupKey !== '') {
    return { kind: 'none', message: NO_TARGET_FOR_BOOK };
  }
  return { kind: 'company', stages: targets.stages ?? {}, rag };
}

export const hasAnyTarget = (stages: StageTargets | undefined): boolean =>
  TARGET_STAGES.some(
    ({ key }) =>
      stages?.[key]?.monthlyCount !== undefined || stages?.[key]?.monthlyValueCents !== undefined
  );

/* ------------------------------------------------------------- window targets */

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;
const utcDay = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * The target for a window, from a monthly target.
 *
 * THE RULE, stated on the band in one line: each month's target counts in full for every whole
 * month the window covers, and pro rata by days for a month it only partly covers. MTD on the 21st
 * of a 30-day month is 21/30 of the monthly target; FYTD on 21 September is July, August and 21/30
 * of September. A window ending on the snapshot is therefore measured against the part of the month
 * the export can see, never against the full month it cannot.
 *
 * Returns null when no monthly target is set, so "no target" can never be mistaken for zero.
 */
export function windowTarget(monthly: number | undefined | null, from: string, to: string): number | null {
  if (monthly === undefined || monthly === null || !Number.isFinite(monthly)) return null;
  if (!ISO.test(from) || !ISO.test(to) || from > to) return null;

  let total = 0;
  const end = utcDay(to);
  let cursor = utcDay(from);
  while (cursor <= end) {
    const d = new Date(cursor);
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const monthDays = daysInMonth(year, month);
    const monthEnd = Date.UTC(year, month - 1, monthDays);
    const sliceEnd = Math.min(monthEnd, end);
    const days = Math.round((sliceEnd - cursor) / DAY_MS) + 1;
    total += (monthly * days) / monthDays;
    cursor = sliceEnd + DAY_MS;
  }
  return total;
}

/** The value target for a window, in whole cents. */
export const windowValueTargetCents = (monthlyCents: number | undefined, from: string, to: string): number | null => {
  const raw = windowTarget(monthlyCents, from, to);
  return raw === null ? null : Math.round(raw);
};

/** A count target for a window, to one decimal: 12.4 deals is a real pro-rated target. */
export const windowCountTarget = (monthlyCount: number | undefined, from: string, to: string): number | null => {
  const raw = windowTarget(monthlyCount, from, to);
  return raw === null ? null : Math.round(raw * 10) / 10;
};

/* ---------------------------------------------------------------- attainment */

export type RagTone = 'green' | 'amber' | 'red';

export const RAG_LABEL: Record<RagTone, string> = {
  green: 'On target',
  amber: 'Watch',
  red: 'Action needed',
};

/**
 * Attainment against a target, and its grade.
 *
 * The percentage is FLOORED and the grade is taken from the floored figure, so the colour always
 * agrees with the number printed beside it: 99.6% prints as 99% and grades amber, rather than
 * printing 100% in an amber box. A zero or absent target grades nothing: there is no honest
 * percentage of zero.
 */
export function attainment(
  actual: number,
  target: number | null,
  rag: RagThresholds = DEFAULT_RAG
): { pct: number; tone: RagTone } | null {
  if (target === null || !Number.isFinite(target) || target <= 0) return null;
  if (!Number.isFinite(actual)) return null;
  const pct = Math.floor((actual / target) * 100);
  const tone: RagTone = pct >= rag.greenAtPct ? 'green' : pct >= rag.amberAtPct ? 'amber' : 'red';
  return { pct, tone };
}

/**
 * One monthly input's grade against its target, or null for no grade at all.
 *
 * No target, a zero target or no figure entered: null, and the tile carries no dot, exactly as a
 * stage with no target carries no target line. All three inputs are higher-is-better, so the
 * stage rule applies unchanged: the entered figure as a percentage of the target, floored, graded
 * by the practice's own thresholds. Company-wide only: these figures are the practice's, not a
 * broker's, so there is no per-broker override to resolve.
 */
export function monthlyInputGrade(
  actual: number | undefined,
  target: number | undefined,
  rag: RagThresholds = DEFAULT_RAG
): { pct: number; tone: RagTone } | null {
  if (actual === undefined || target === undefined) return null;
  return attainment(actual, target, rag);
}

/* ------------------------------------------------- average deal, net settlements, referrers */

export interface TargetGrade {
  pct: number;
  tone: RagTone;
}

/**
 * One figure against one target, both in the same unit, graded by the practice's thresholds.
 *
 * The rule every brief-gaps target shares: no target, a zero target or no figure grades nothing
 * (null), so an absent target can be rendered as "No target set" and never as zero or green. A
 * level (the average deal, the loan book) is compared as it stands; a flow is compared against a
 * window target the caller has already pro-rated with windowValueTargetCents / windowCountTarget.
 */
export function gradeAgainstTarget(
  actual: number | null | undefined,
  target: number | null | undefined,
  rag: RagThresholds = DEFAULT_RAG
): TargetGrade | null {
  if (actual === null || actual === undefined || target === null || target === undefined) return null;
  return attainment(actual, target, rag);
}

/**
 * The average deal target for the view on screen: a broker's own, never the company's in its
 * place (the company average is the whole practice's, and one broker set against it is graded on a
 * number they were never meant to meet). Undefined when none is set, or when no document loaded.
 */
export function averageDealTarget(
  targets: TargetsDoc | null | undefined,
  scope: { adviserId?: string | null } = {}
): number | undefined {
  if (!targets) return undefined;
  if (scope.adviserId !== undefined && scope.adviserId !== null && scope.adviserId !== '') {
    return targets.byBroker?.[scope.adviserId]?.averageDealCents;
  }
  return targets.averageDealCents;
}

/** A referrer's stage targets, or null when none is set for that partner key. */
export function partnerTargetsFor(targets: TargetsDoc | null | undefined, partnerKey: string): StageTargets | null {
  const own = targets?.byPartner?.[partnerKey];
  return own && hasAnyTarget(own) ? own : null;
}

/**
 * A Leads & Partners partner row, read structurally so this module keeps its no-imports rule. The
 * V6 stage columns are optional on an older payload, and a stage the payload does not carry is not
 * graded rather than graded as zero.
 */
export interface PartnerRowFlows {
  deals: number;
  loanAmountCents: number;
  lodged?: number;
  lodgedLoanAmountCents?: number;
  approved?: number;
  approvedLoanAmountCents?: number;
  settled: number;
  settledLoanAmountCents: number;
}

/** The four stage flows of a partner row, keyed as targets are. */
export function partnerRowFlows(row: PartnerRowFlows): Partial<Record<TargetStageKey, FlowFigure>> {
  const out: Partial<Record<TargetStageKey, FlowFigure>> = {
    leads: { count: row.deals, valueCents: row.loanAmountCents },
    settlements: { count: row.settled, valueCents: row.settledLoanAmountCents },
  };
  if (row.lodged !== undefined) out.submissions = { count: row.lodged, valueCents: row.lodgedLoanAmountCents ?? 0 };
  if (row.approved !== undefined) out.approvals = { count: row.approved, valueCents: row.approvedLoanAmountCents ?? 0 };
  return out;
}

export interface StageGrade {
  /** The window target in deals (one decimal) or null when no count target is set. */
  countTarget: number | null;
  /** The window target in whole cents or null when no dollar target is set. */
  valueTargetCents: number | null;
  count: TargetGrade | null;
  value: TargetGrade | null;
}

/**
 * A referrer's four stages against their targets over a creation window.
 *
 * Stage targets are monthly flows, so each is pro-rated to the window by the band's rule
 * (windowTarget) before grading, exactly as the Deal movements tiles are. A stage with no target
 * in either unit, or no figure in the row, grades nothing. Pure, so workstream B renders it on the
 * partner rows and the single referrer view without re-deriving the rule.
 */
export function gradePartnerStages(
  stages: StageTargets | null | undefined,
  actuals: Partial<Record<TargetStageKey, FlowFigure>>,
  window: { from: string; to: string },
  rag: RagThresholds = DEFAULT_RAG
): Record<TargetStageKey, StageGrade> {
  const out = {} as Record<TargetStageKey, StageGrade>;
  for (const { key } of TARGET_STAGES) {
    const target = stages?.[key];
    const actual = actuals[key];
    const countTarget = windowCountTarget(target?.monthlyCount, window.from, window.to);
    const valueTargetCents = windowValueTargetCents(target?.monthlyValueCents, window.from, window.to);
    out[key] = {
      countTarget,
      valueTargetCents,
      count: actual ? gradeAgainstTarget(actual.count, countTarget, rag) : null,
      value: actual ? gradeAgainstTarget(actual.valueCents, valueTargetCents, rag) : null,
    };
  }
  return out;
}

/* --------------------------------------------------------------------- months */

/** The 12 calendar months ending at the snapshot's month, newest first. */
export function recentMonthKeys(asAtDate: string, count = 12): string[] {
  if (!ISO.test(asAtDate)) return [];
  const [y, m] = asAtDate.split('-').map(Number);
  const out: string[] = [];
  for (let back = 0; back < count; back += 1) {
    const index = m - 1 - back;
    const year = y + Math.floor(index / 12);
    const month = ((index % 12) + 12) % 12 + 1;
    out.push(`${year}-${String(month).padStart(2, '0')}`);
  }
  return out;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'Sep 2026'. */
export function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return key;
  return `${MONTH_ABBR[m - 1]} ${y}`;
}

/* -------------------------------------------------------------- editor inputs */

/**
 * Whole dollars typed into a box, to integer cents. Blank is "not set" (undefined), never zero.
 * Anything that is not a non-negative whole number is refused (null) so the editor can say so
 * before the server does.
 */
export function dollarsInputToCents(text: string): number | undefined | null {
  const trimmed = text.replace(/[$,\s]/g, '');
  if (trimmed === '') return undefined;
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed) * 100;
}

/** A non-negative whole number, blank as undefined, anything else refused (null). */
export function wholeNumberInput(text: string, max = Number.MAX_SAFE_INTEGER): number | undefined | null {
  const trimmed = text.replace(/[,\s]/g, '');
  if (trimmed === '') return undefined;
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= max ? value : null;
}

/** A decimal between min and max inclusive, blank as undefined, anything else refused (null). */
export function boundedDecimalInput(text: string, min: number, max: number): number | undefined | null {
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= min && value <= max ? value : null;
}

export const centsToDollarsInput = (cents: number | undefined): string =>
  cents === undefined ? '' : String(Math.round(cents / 100));
